import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { mapAPI } from "services/mapSlice";
import type {
  ExposedLimitInterpretation,
  ExposedLimitPerilCode,
} from "src/ts-types/ApiTypes";

const EXPOSED_LIMIT_INTERPRETATION_KEY = "exposedLimitInterpretation";

export type PortfolioMapMetric = "tiv" | ExposedLimitPerilCode;

/** State that only Ping Portfolio maps use. Regular maps keep `isPortfolio` false and never read the rest. */
type PortfolioState = {
  // From the policy overview: the sovid is a Ping Portfolio.
  isPortfolio: boolean;
  /**
   * Deductible composition for exposed limits. A rarely changed default, set from the exposed
   * limits settings menu and remembered per browser.
   */
  exposedLimitInterpretation: ExposedLimitInterpretation;
  // What the map colours by: TIV, or one peril's exposed limit.
  mapMetric: PortfolioMapMetric;
  // County overlay (Overlays → Admin → County). At most one of it and the State overlay is shown.
  showCountyLayer: boolean;
};

const initialState: PortfolioState = {
  isPortfolio: false,
  exposedLimitInterpretation:
    localStorage.getItem(EXPOSED_LIMIT_INTERPRETATION_KEY) === "greater_of"
      ? "greater_of"
      : "additive",
  mapMetric: "tiv",
  showCountyLayer: false,
};

const portfolioSlice = createSlice({
  name: "portfolio",
  initialState,
  reducers: {
    setExposedLimitInterpretation(
      state,
      action: PayloadAction<ExposedLimitInterpretation>,
    ) {
      state.exposedLimitInterpretation = action.payload;
      localStorage.setItem(EXPOSED_LIMIT_INTERPRETATION_KEY, action.payload);
    },
    setMapMetric(state, action: PayloadAction<PortfolioMapMetric>) {
      state.mapMetric = action.payload;
    },
    setShowCountyLayer(state, action: PayloadAction<boolean>) {
      state.showCountyLayer = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addMatcher(
      mapAPI.endpoints.getPolicyOverview.matchFulfilled,
      (state, action) => {
        state.isPortfolio = action.payload?.is_portfolio === true;
      },
    );
  },
});

export const { setExposedLimitInterpretation, setMapMetric, setShowCountyLayer } =
  portfolioSlice.actions;

export default portfolioSlice.reducer;
